/**
 * Light / dark / follow-the-system.
 *
 * The initial class is set by an inline script in index.html before first
 * paint, so this module must agree with it exactly — hence one shared key and
 * one shared resolution rule. Getting them out of step causes a flash of the
 * wrong theme on every cold load.
 */

export type ThemeChoice = 'light' | 'dark' | 'system'

export const THEME_KEY = 'mountit.theme'

function readStored(): ThemeChoice {
  try {
    const value = localStorage.getItem(THEME_KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    // Safari in private mode throws on localStorage access rather than
    // returning null. Following the system is the right fallback.
    return 'system'
  }
}

class ThemeStore {
  choice = $state<ThemeChoice>(readStored())
  private media = window.matchMedia('(prefers-color-scheme: dark)')

  constructor() {
    this.media.addEventListener('change', () => {
      if (this.choice === 'system') this.apply()
    })
    this.apply()
  }

  get resolved(): 'light' | 'dark' {
    return this.choice === 'system' ? (this.media.matches ? 'dark' : 'light') : this.choice
  }

  set(choice: ThemeChoice): void {
    this.choice = choice
    try {
      if (choice === 'system') localStorage.removeItem(THEME_KEY)
      else localStorage.setItem(THEME_KEY, choice)
    } catch {
      /* Storage denied. The choice still applies for this page load. */
    }
    this.apply()
  }

  /** Cycle in the order a user expects from a single toggle button. */
  cycle(): void {
    this.set(this.resolved === 'dark' ? 'light' : 'dark')
  }

  private apply(): void {
    const resolved = this.resolved
    document.documentElement.dataset.theme = resolved
    document.documentElement.style.colorScheme = resolved
  }
}

export const theme = new ThemeStore()
